import React from 'react'
import { Box, Typography, Paper, Stack, Button, Alert } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import BookingHeader from './BookingHeader'

interface BookingIntroProps {
  onStart: () => void
  hasToken: boolean
}

// Shown before the booking flow begins. Surfaces the security/usage guidance and, on Continue,
// hands control back so the session exchange (and its short-lived clock) starts only then.
const BookingIntro: React.FC<BookingIntroProps> = ({ onStart, hasToken }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        overflow: 'hidden',
        borderRadius: '18px',
        bgcolor: '#FBFAF7',
        border: '1px solid #E7E4DB',
        boxShadow: '0 24px 60px -30px rgba(0,42,58,0.35)'
      }}
    >
      <BookingHeader title="Book Your Meeting" />

      {/* Body */}
      <Box sx={{ px: { xs: 3, sm: 4 }, py: 3.5 }}>
        <Typography
          sx={{ color: '#84744D', fontWeight: 600, fontSize: '0.75rem', letterSpacing: '0.16em', textTransform: 'uppercase', display: 'block', mb: 3 }}
        >
          Before You Begin
        </Typography>

        <Stack spacing={3.25}>
          <IntroPoint
            icon={<TimerOutlinedIcon sx={{ fontSize: 24, color: '#007680' }} />}
            title="Complete in one session"
            desc="For your security, your booking session is time-limited. Please finish your booking once you begin."
          />
          <IntroPoint
            icon={<RefreshOutlinedIcon sx={{ fontSize: 24, color: '#B49D77' }} />}
            title="Don't refresh this page"
            desc="Don't refresh or close this page while booking. If you do, you'll need to open the link from your email again."
          />
          <IntroPoint
            icon={<LockOutlinedIcon sx={{ fontSize: 24, color: '#002A3A' }} />}
            title="This link is personal"
            desc="This link is just for you — please don't forward or share it."
          />
        </Stack>

        {hasToken ? (
          <Box sx={{ mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={onStart}
              endIcon={<ArrowForwardIcon />}
              sx={{
                py: 1.75,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: '12px',
                textTransform: 'none',
                bgcolor: '#002A3A',
                color: '#F3F1EC',
                boxShadow: 'none',
                transition: 'background 0.2s ease',
                '&:hover': { bgcolor: '#013a50', boxShadow: 'none' }
              }}
            >
              Continue
            </Button>
          </Box>
        ) : (
          <Alert severity="error" sx={{ mt: 3, borderRadius: '11px' }}>
            No booking token found. Please use the link from your email.
          </Alert>
        )}
      </Box>
    </Paper>
  )
}

function IntroPoint({ icon, title, desc }: Readonly<{ icon: React.ReactNode; title: string; desc: string }>) {
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <Box sx={{ mt: 0.25, flexShrink: 0 }}>{icon}</Box>
      <Box>
        <Typography sx={{ fontWeight: 600, fontSize: '1.05rem', color: '#002A3A', lineHeight: 1.3 }}>
          {title}
        </Typography>
        <Typography sx={{ fontWeight: 300, fontSize: '0.94rem', color: '#5A6567', lineHeight: 1.6, mt: 0.5 }}>
          {desc}
        </Typography>
      </Box>
    </Box>
  )
}

export default BookingIntro
